import { create } from 'zustand';
import axios from 'axios';

// 기존 backend-api URL (config 기능은 v2 미구현으로 레거시 API 사용)
const LEGACY_API_URL = `${import.meta.env.VITE_SERVER_URL}`;

// Legacy API 인스턴스 - 역, 열차종별, 좌석 설정 등 v2 미구현 기능용
const legacyApi = axios.create({
  baseURL: LEGACY_API_URL,
});
legacyApi.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem('auth-storage'))?.state?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const useConfigStore = create((set, get) => ({
  // State
  stations: [],
  trainTypes: [],
  trains: [],
  seats: [],
  isLoading: false,
  error: null,

  // === Station Management ===

  fetchStations: async (includeDeleted = false) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/config/stations', {
        params: { include_deleted: includeDeleted }
      });
      set({ stations: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '역 목록 조회 실패', isLoading: false });
    }
  },

  getStation: async (seqNum) => {
    try {
      const { data } = await legacyApi.get(`/config/stations/${seqNum}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '역 조회 실패');
    }
  },

  createStation: async (stationData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/config/stations', stationData);
      await get().fetchStations();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역 생성 실패');
    }
  },

  updateStation: async (seqNum, stationData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/config/stations/${seqNum}`, stationData);
      await get().fetchStations();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역 수정 실패');
    }
  },

  deleteStation: async (seqNum, hardDelete = false) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/config/stations/${seqNum}`, {
        params: { hard_delete: hardDelete }
      });
      await get().fetchStations();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역 삭제 실패');
    }
  },

  // === Train Type Management ===

  fetchTrainTypes: async (includeDeleted = false) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/config/train-types', {
        params: { include_deleted: includeDeleted }
      });
      set({ trainTypes: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '열차 종별 조회 실패', isLoading: false });
    }
  },

  getTrainType: async (seqNum) => {
    try {
      const { data } = await legacyApi.get(`/config/train-types/${seqNum}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '열차 종별 조회 실패');
    }
  },

  createTrainType: async (typeData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/config/train-types', typeData);
      await get().fetchTrainTypes();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 종별 생성 실패');
    }
  },

  updateTrainType: async (seqNum, typeData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/config/train-types/${seqNum}`, typeData);
      await get().fetchTrainTypes();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 종별 수정 실패');
    }
  },

  deleteTrainType: async (seqNum, hardDelete = false) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/config/train-types/${seqNum}`, {
        params: { hard_delete: hardDelete }
      });
      await get().fetchTrainTypes();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 종별 삭제 실패');
    }
  },

  // === Train (Rail Info) Management ===

  fetchTrains: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/config/trains', { params });
      set({ trains: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '열차 정보 조회 실패', isLoading: false });
    }
  },

  getTrain: async (seqNum) => {
    try {
      const { data } = await legacyApi.get(`/config/trains/${seqNum}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '열차 정보 조회 실패');
    }
  },

  createTrain: async (trainData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/config/trains', trainData);
      await get().fetchTrains();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 정보 생성 실패');
    }
  },

  updateTrain: async (seqNum, trainData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/config/trains/${seqNum}`, trainData);
      await get().fetchTrains();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 정보 수정 실패');
    }
  },

  deleteTrain: async (seqNum) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/config/trains/${seqNum}`);
      await get().fetchTrains();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '열차 정보 삭제 실패');
    }
  },

  // === Seat (Rail Info Ticket) Management ===

  fetchSeats: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/config/seats', { params });
      set({ seats: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '좌석 정보 조회 실패', isLoading: false });
    }
  },

  getSeat: async (seqNum) => {
    try {
      const { data } = await legacyApi.get(`/config/seats/${seqNum}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '좌석 정보 조회 실패');
    }
  },

  createSeat: async (seatData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/config/seats', seatData);
      await get().fetchSeats();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '좌석 정보 생성 실패');
    }
  },

  updateSeat: async (seqNum, seatData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/config/seats/${seqNum}`, seatData);
      await get().fetchSeats();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '좌석 정보 수정 실패');
    }
  },

  deleteSeat: async (seqNum) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/config/seats/${seqNum}`);
      await get().fetchSeats();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '좌석 정보 삭제 실패');
    }
  },

  // === Clear Error ===

  clearError: () => set({ error: null }),
}));

export default useConfigStore;
